
import React, { useState } from 'react';
import { addEmail } from '../api/parentCoach';

export default function ParentCoachLanding() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (email && email.includes('@')) {
      setIsLoading(true);
      setError(null);

      try {
        await addEmail({ email });
        setIsSubmitted(true);
        setIsLoading(false);
      } catch (err) {
        console.error('Erreur lors de l\'envoi de l\'email:', err);
        setError('Une erreur est survenue. Veuillez réessayer.');
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-700 to-slate-900 text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-light mb-3 opacity-90">par Oria</p>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
            Accompagnez sereinement l'orientation de votre ado
          </h1>
          <p className="text-lg md:text-xl font-light opacity-95 max-w-3xl mx-auto">
            L'assistant intelligent disponible 24h/24 qui vous aide à guider votre adolescent dans ses choix d'orientation
          </p>
        </div>
      </header>

      {/* Section: La situation */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">

              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-3xl font-bold text-slate-700">🕐 La situation</h2>
              </div>
              <p className="text-lg text-gray-600 mb-5">
                Quand vient le moment d'accompagner votre ado dans son orientation, <strong className="text-slate-800">le temps manque</strong>.
              </p>
              <p className="text-lg text-gray-600 mb-5">
                Entre la 3ème et la terminale, les choix s'accumulent : quel lycée ? quelles spécialités garder ? quelles formations viser sur Parcoursup ?
              </p>
              <p className="text-lg text-gray-600">
                Vous voulez bien faire. Vous voulez être présent. Mais explorer les filières, comparer les programmes, comprendre les débouchés… <strong className="text-slate-800">tout cela prend des heures</strong> que vous n'avez pas.
              </p>
            </div>
            <div className="w-full md:w-5/12 md:max-w-lg">
              <img 
                src="/assets/situation.jpg" 
                alt="Étudiant travaillant sur son orientation"
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section: Les conséquences */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          
          <div className="flex flex-col md:flex-row-reverse items-center gap-12">
            <div className="flex-1">

              <div className="flex items-center gap-4 mb-8">
                <h2 className="text-3xl font-bold text-slate-700">⚠️ Les conséquences</h2>
              </div>
              <p className="text-lg text-gray-600 mb-5">
                Ce manque de temps génère <strong className="text-slate-800">du stress</strong> à la maison.
              </p>
              <p className="text-lg text-gray-600 mb-5">
                Les échanges sur l'avenir deviennent une source d'inquiétude.
              </p>
              <p className="text-lg text-gray-600">
                <strong className="text-slate-800">Sans les bonnes informations</strong> — et sans savoir comment les partager avec votre ado — vous risquez de vous sentir démuni au moment où il a le plus besoin de vous. Vous hésitez sur les questions à lui poser, sur la meilleure façon de l'aider à avancer dans sa réflexion.
              </p>
            </div>
            <div className="w-full md:w-5/12 md:max-w-lg">
              <img 
                src="/assets/probleme.jpg" 
                alt="Parent et adolescent en discussion"
                className="w-full h-80 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section: La solution */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <h2 className="text-3xl font-bold text-slate-700">💡 La solution</h2>
          </div>
          
          <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white p-10 md:p-12 rounded-2xl shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-12 mb-8">
              <div className="flex-1">
                <p className="text-lg text-white/95 mb-5">
                  <strong className="text-white">ParentCoach</strong> est un assistant disponible <strong className="text-white">24h/24 et 7j/7 sur WhatsApp</strong>, conçu pour vous aider à <strong className="text-white">accompagner sereinement votre ado</strong> dans son orientation.
                </p>
                <p className="text-lg text-white/95 mb-5">
                  Développé <strong className="text-white">avec des experts et des coachs en orientation scolaire</strong>, ParentCoach combine l'intelligence artificielle à une <strong className="text-white">base de données spécialisée regroupant plus de 25 000 formations en France</strong>.
                </p>
                <p className="text-lg text-white/95">
                  Vous posez votre question directement sur WhatsApp — ParentCoach vous répond instantanément avec des <strong className="text-white">réponses fiables, précises et personnalisées</strong>.
                </p>
              </div>
              <div className="w-full md:w-5/12 md:max-w-md">
                <img 
                  src="/assets/solution.jpg" 
                  alt="Conversation sur smartphone"
                  className="w-full h-70 object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>
            
            <p className="text-lg text-white/95 mb-5">
              Contrairement à ChatGPT, ParentCoach s'appuie sur des ressources vérifiées et l'expertise de professionnels de l'orientation : <strong className="text-white">beaucoup moins d'erreurs, beaucoup plus de pertinence</strong>.
            </p>
            <p className="text-lg text-white/95 mb-5">
              <strong className="text-white">Mais ParentCoach ne vous donne pas seulement des informations — il vous apprend à accompagner.</strong>
            </p>
            <p className="text-lg text-white/95">
              Vous recevez non seulement les réponses sur les formations et filières, mais aussi des conseils sur <strong className="text-white">comment en parler avec votre ado, quelles questions lui poser pour l'aider à clarifier sa motivation, quelle posture adopter</strong> — pour qu'il fasse des choix éclairés qui lui ressemblent, sans que vous ayez à décider à sa place.
            </p>
          </div>
        </div>
      </section>

      {/* Section: Les résultats */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <h2 className="text-3xl font-bold text-slate-700">🎯 Les résultats concrets pour vous et votre ado</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-100 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-blue-50 text-center">
              <div className="text-5xl mb-6">⏱️</div>
              <h3 className="text-xl font-bold text-slate-700 mb-4">Gagner du temps et de la clarté</h3>
              <p className="text-gray-600 bg-white p-5 rounded-xl leading-relaxed">
                Terminées, les heures perdues à chercher : vous disposez d'une roadmap de la 3ème à la terminale et de réponses instantanées à vos questions concrètes.
              </p>
            </div>
            
            <div className="bg-gray-100 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-blue-50 text-center">
              <div className="text-5xl mb-6">🤝</div>
              <h3 className="text-xl font-bold text-slate-700 mb-4">Retrouver une relation apaisée</h3>
              <p className="text-gray-600 bg-white p-5 rounded-xl leading-relaxed">
                Apprenez à poser les questions qui ouvrent le dialogue, à guider sans prendre le volant, et à faire de l'orientation un sujet de complicité plutôt que de tension.
              </p>
            </div>
            
            <div className="bg-gray-100 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-blue-50 text-center">
              <div className="text-5xl mb-6">📊</div>
              <h3 className="text-xl font-bold text-slate-700 mb-4">Avancer avec méthode et assurance</h3>
              <p className="text-gray-600 bg-white p-5 rounded-xl leading-relaxed">
                Appuyez-vous sur des informations fiables pour repérer les pistes et formations qui correspondent vraiment au profil de votre ado.
              </p>
            </div>
            
            <div className="bg-gray-100 p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 border-2 border-blue-50 text-center">
              <div className="text-5xl mb-6">✨</div>
              <h3 className="text-xl font-bold text-slate-700 mb-4">Transformer le stress en dynamique positive</h3>
              <p className="text-gray-600 bg-white p-5 rounded-xl leading-relaxed">
                ParentCoach remplace l'anxiété par une démarche structurée qui avance, semaine après semaine, vers plus de sérénité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Offre */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white p-10 md:p-12 rounded-2xl shadow-xl">
            <div className="text-5xl mb-6 text-center">🎉</div>
            <h2 className="text-3xl font-bold mb-6 text-center">Offre de lancement réservée aux 100 premiers parents</h2>
            <p className="text-lg mb-8 opacity-95 text-center">
              Profitez d'<strong>avantages exclusifs</strong> avant le lancement officiel :
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <span className="text-2xl text-blue-300 flex-shrink-0">✓</span>
                <div>
                  <p className="text-lg"><strong>Test gratuit</strong> de ParentCoach</p>
                  <p className="text-sm opacity-90">Découvrez l'assistant en avant-première, sans engagement.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <span className="text-2xl text-blue-300 flex-shrink-0">✓</span>
                <div>
                  <p className="text-lg"><strong>50% pendant 1 an</strong> sur votre abonnement</p>
                  <p className="text-sm opacity-90">Offre unique, valable uniquement pour les 100 premiers inscrits.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <span className="text-2xl text-blue-300 flex-shrink-0">✓</span>
                <div>
                  <p className="text-lg"><strong>Accès prioritaire</strong> aux nouvelles fonctionnalités</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm px-2 py-4 rounded-xl text-center">
              <p className="text-lg font-semibold">
                ⚡️ Seulement 100 places disponibles, après ces avantages disparaissent définitivement.
              </p>
            </div>
            
            <p className="text-center mt-8 text-lg opacity-95">
              Ne ratez pas le lancement de l'assistant qui va transformer l'orientation à la maison.
            </p>
          </div>
        </div>
      </section>

      {/* Section CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-700 to-slate-900 text-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Réservez votre place parmi les 100 premiers</h2>
          <p className="text-lg mb-8 opacity-95">
            L'inscription sur la liste d'attente est gratuite et sans engagement.
          </p>
          
          <div className="max-w-md mx-auto space-y-4">
            {!isSubmitted && (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="Votre adresse email"
                className="w-full px-6 py-4 text-lg rounded-lg bg-white/95 text-slate-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
              />
            )}
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <button
              onClick={handleSubmit}
              disabled={!email || !email.includes('@') || isLoading || isSubmitted}
              className={`w-full ${isSubmitted ? 'bg-orange-600' : 'bg-orange-600 hover:bg-orange-700'} disabled:cursor-not-allowed text-white font-bold py-4 px-8 rounded-lg text-lg transition-all ${!isSubmitted && 'transform hover:-translate-y-0.5'} shadow-lg ${!isSubmitted && 'hover:shadow-xl'}`}
            >
              {isLoading ? 'Envoi en cours...' : isSubmitted ? '✓ Demande envoyée !' : 'Je réserve ma place'}
            </button>
          </div>
          
          <p className="text-sm mt-6 opacity-90">
            Vous recevrez un accès prioritaire dès l'ouverture de ParentCoach et pourrez tester gratuitement avant de décider.
          </p>
          
          <p className="mt-10 text-lg font-semibold">
            Ne laissez pas passer cette opportunité unique d'accompagner sereinement l'orientation de votre ado, tout en bénéficiant d'avantages qui ne seront plus jamais proposés.
          </p>
          
          <p className="italic mt-8 opacity-90">
            Parce que vous voulez être présent pour votre ado — sans y passer des heures.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-12 px-6 text-center">
        <p className="font-semibold text-lg mb-2">ParentCoach | Oria Coach</p>
        <p className="text-sm opacity-80">Accompagnez sereinement l'orientation de votre adolescent</p>
      </footer>
    </div>
  );
}